import { Injectable, inject, signal, computed } from '@angular/core';
import { SsoCryptoService } from './sso-crypto.service';

/** PAT-203 — chat UI font scale preference (local only). */
export type FontSizePreference = 's' | 'm' | 'l';

const STORAGE_KEY = 'pathways-font-size';

@Injectable({ providedIn: 'root' })
export class FontSizeService {
  // PAT-385 — declared first so inject() is available when stored signal initialises.
  private readonly crypto = inject(SsoCryptoService);

  private readonly stored = signal<FontSizePreference>(this.readStored());

  /** Current font size preference; defaults to medium. */
  fontSize = computed(() => this.stored());

  constructor() {
    if (typeof document !== 'undefined') {
      this.applyToDocument();
    }
  }

  setFontSize(value: FontSizePreference): void {
    this.stored.set(value);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, value);
      } catch { /* ignore quota / private-mode errors */ }
    }
    this.applyToDocument();
  }

  private readStored(): FontSizePreference {
    if (typeof localStorage === 'undefined') return 'm';
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'm';
    let v: string;
    try {
      v = this.crypto.decryptAES(raw).trim();
      if (v === 's' || v === 'm' || v === 'l') {
        try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
      }
    } catch {
      v = raw;
    }
    if (v === 's' || v === 'm' || v === 'l') return v;
    return 'm';
  }

  /** Apply root font size (12 / 14 / 16px) via html[data-font-size] and body class. */
  applyToDocument(value?: FontSizePreference): void {
    const size = value ?? this.fontSize();
    const doc = typeof document !== 'undefined' ? document : null;
    if (!doc) return;

    doc.body.classList.remove('font-size-s', 'font-size-m', 'font-size-l');
    doc.body.classList.add(`font-size-${size}`);
    doc.documentElement.setAttribute('data-font-size', size);
  }
}
